import { OverlayContainer } from '@angular/cdk/overlay';
import {
    APP_BASE_HREF,
    DatePipe,
    DecimalPipe,
    DOCUMENT,
    LowerCasePipe,
} from '@angular/common';
import {
    HTTP_INTERCEPTORS,
    provideHttpClient,
    withInterceptorsFromDi,
} from '@angular/common/http';
import {
    APP_INITIALIZER,
    ApplicationConfig,
    ErrorHandler,
    importProvidersFrom,
} from '@angular/core';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import {
    MAT_SNACK_BAR_DEFAULT_OPTIONS,
    MatSnackBarModule,
} from '@angular/material/snack-bar';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
    provideRouter,
    Router,
    withInMemoryScrolling,
    withRouterConfig,
} from '@angular/router';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import {
    AuthInterceptor,
    TokenService,
} from '@polaris-customerweb/sharedcomponents.auth';
import { SkuInterceptor } from '@polaris-customerweb/sharedcomponents.config';
import {
    LanguageInterceptor,
    WebComponentConfigOptions,
} from '@polaris-customerweb/sharedcomponents.core';
import { provideSharedComponents } from '@polaris-customerweb/sharedcomponents.shared';
import * as Sentry from '@sentry/angular';
import { AngularSvgIconModule, SvgLoader } from 'angular-svg-icon';
import { environment } from 'src/environments/environment';
import { AngularSvgIconCustomLoader } from './angular-svg-icon-custom-loader';
import { AppOverlayContainer } from './app-overlay-container';
import { CULTURE_CODE, IS_EPISERVER } from './app.constants';
import { routes } from './app.routes';
import { adventureStoreModule } from './components/adventure/adventure.reducer';
import { collectionStoreModule } from './components/common/collection/collection.reducer';
import { rideEventStoreModule } from './components/common/ride-event/ride-event.reducer';
import { communityItemStoreModule } from './components/community/activity/community-item.reducer';
import { communityFavoriteStoreModule } from './components/community/community-favorite/community-favorite.reducer';
import { dealerStoreModule } from './components/dealer/dealer.reducer';
import { eulaStoreModule } from './components/eula/eula.reducer';
import { geofenceStoreModule } from './components/geofence/geofence.reducer';
import { displayStoreModule } from './components/maintenance/display/update/display.reducer';
import { mobileSidenavStoreModule } from './components/mobile-sidenav/mobile-sidenav.reducer';
import { parcelStoreModule } from './components/parcels/parcels.reducer';
import { plannedRideStoreModule } from './components/planned-ride/planned-ride.reducer';
import { placeStoreModule } from './components/point-of-interest/point-of-interest.reducer';
import { rideAreaStoreModule } from './components/ride-area/ride-area.reducer';
import { rideConditionStoreModule } from './components/ride-condition/ride-condition.reducer';
import { rideGroupStoreModule } from './components/ride-group/ride-group.reducer';
import { userContentStoreModule } from './components/rides-places/rides-places.reducer';
import { shareStoreModule } from './components/share/share.reducer';
import { trackedRideStoreModule } from './components/tracked-ride/tracked-ride.reducer';
import { waypointStoreModule } from './components/waypoint/waypoint.reducer';
import { RemoveWhiteSpacePipe } from './pipes/remove-whitespace.pipe';
import { appSkuFactory } from './state/app-config/factories/app-sku.factory';
import { isEpiserverFactory } from './state/app-config/factories/is-episerver.factory';
import { WINDOW } from './state/app-config/factories/window.factory';
import { clubStoreModule } from './state/club/club.reducer';
import { communityFilterStoreModule } from './state/community/community-filter/community-filter.reducer';
import { communityListStoreModule } from './state/community/community-list/community-list.reducer';
import { cultureStoreModule } from './state/culture/culture.reducer';
import { cultureCodeFactory } from './state/culture/initializer/culture-code.initializer';
import { equipmentEffectsModule } from './state/equipment/equipment.effects';
import { equipmentStoreModule } from './state/equipment/equipment.reducer';
import { garageEffectsModule } from './state/garage/garage.effects';
import { garageStoreModule } from './state/garage/garage.reducer';
import { groupRideMessageEffectsModule } from './state/group-ride/group-ride-message/group-ride-message.effects';
import { groupRideMessageStoreModule } from './state/group-ride/group-ride-message/group-ride-message.reducer';
import { groupRideStoreModule } from './state/group-ride/group-ride.reducer';
import { layerVisibilityEffectsModule } from './state/map/layer-visibility/layer-visibility.effects';
import { layerVisibilityStoreModule } from './state/map/layer-visibility/layer-visibility.reducer';
import { mapStoreModule } from './state/map/map.reducer';
import { appBaseHrefFactory } from './state/routing/app-base-href.factory';
import { searchStoreModule } from './state/search/search.reducer';
import { TokenService as CustomTokenService } from './state/user-auth/token.service';
import { userAuthStoreModule } from './state/user-auth/user-auth.reducer';
import { userLocationEffectsModule } from './state/user-location/user-location.effects';
import { userLocationStoreModule } from './state/user-location/user-location.reducer';
import { vehicleLocationEffectsModule } from './state/vehicle/vehicle-location/vehicle-location.effects';
import { vehicleLocationStoreModule } from './state/vehicle/vehicle-location/vehicle-location.reducer';
import { vehicleEffectsModule } from './state/vehicle/vehicle.effects';
import { vehicleStoreModule } from './state/vehicle/vehicle.reducer';

export const getBootstrapConfig: (
    assetCdnUrl: string,
    cultureCode: string,
    environment: string,
    isEmbedded: boolean
) => Promise<ApplicationConfig> = async (
    assetCdnUrl,
    cultureCode,
    currentEnvironment,
    isEmbedded
) => {
    const appSku = appSkuFactory(document);
    const config = await fetch(
        `${assetCdnUrl}assets/js/webcomponents-config.json?v=${String(Date.now())}`
    )
        .then(
            (response) => response.json() as Promise<WebComponentConfigOptions>
        )
        .then((configFromJson) => {
            return {
                ...configFromJson,
                appSku,
                assetCdnUrl,
                baseHref: appBaseHrefFactory(isEmbedded, cultureCode),
                cultureCode,
                environment: currentEnvironment,
                extClientId: `web-rc-${appSku}`,
                isEmbedded,
                siteUrl: `${window.document.location.origin}/${cultureCode.toLowerCase()}/`,
                version: environment.version,
            } as WebComponentConfigOptions;
        });

    const bootstrapConfig: ApplicationConfig = {
        providers: [
            provideSharedComponents(config),
            importProvidersFrom(
                // required for service injection
                MatDialogModule,
                MatSnackBarModule,
                /********** 3rd Party Modules **************/
                AngularSvgIconModule.forRoot({
                    loader: {
                        provide: SvgLoader,
                        useClass: AngularSvgIconCustomLoader,
                    },
                }),
                // ServiceWorkerModule.register('ngsw-worker.js', {
                //     enabled: environment.production,
                // }),
                /******** NGRX/Store Modules  *************/
                clubStoreModule,
                collectionStoreModule,
                communityFavoriteStoreModule,
                communityFilterStoreModule,
                communityItemStoreModule,
                communityListStoreModule,
                cultureStoreModule,
                dealerStoreModule,
                adventureStoreModule,
                displayStoreModule,
                equipmentStoreModule,
                eulaStoreModule,
                garageStoreModule,
                geofenceStoreModule,
                groupRideStoreModule,
                groupRideMessageStoreModule,
                layerVisibilityStoreModule,
                mapStoreModule,
                mobileSidenavStoreModule,
                placeStoreModule,
                plannedRideStoreModule,
                rideAreaStoreModule,
                rideConditionStoreModule,
                rideEventStoreModule,
                rideGroupStoreModule,
                searchStoreModule,
                shareStoreModule,
                trackedRideStoreModule,
                userContentStoreModule,
                userLocationStoreModule,
                vehicleLocationStoreModule,
                vehicleStoreModule,
                waypointStoreModule,
                parcelStoreModule,
                userAuthStoreModule,
                StoreModule.forRoot(
                    {},
                    {
                        runtimeChecks: {
                            strictActionImmutability: false,
                            strictStateImmutability: false,
                        },
                    }
                ),
                StoreDevtoolsModule.instrument({
                    connectInZone: true,
                    logOnly: environment.production,
                    maxAge: 25,
                    name: 'rdx',
                }),
                /******** NGRX/Effects Modules  *************/
                equipmentEffectsModule,
                garageEffectsModule,
                groupRideMessageEffectsModule,
                layerVisibilityEffectsModule,
                userLocationEffectsModule,
                vehicleEffectsModule,
                vehicleLocationEffectsModule,
                EffectsModule.forRoot([])
            ),
            DatePipe,
            DecimalPipe,
            LowerCasePipe,
            RemoveWhiteSpacePipe,
            {
                deps: [IS_EPISERVER, CULTURE_CODE],
                provide: APP_BASE_HREF,
                useFactory: appBaseHrefFactory,
            },
            {
                deps: [WINDOW],
                provide: CULTURE_CODE,
                useFactory: cultureCodeFactory,
            },
            {
                multi: true,
                provide: HTTP_INTERCEPTORS,
                useClass: LanguageInterceptor,
            },
            {
                multi: true,
                provide: HTTP_INTERCEPTORS,
                useClass: SkuInterceptor,
            },
            {
                multi: true,
                provide: HTTP_INTERCEPTORS,
                useClass: AuthInterceptor,
            },
            {
                provide: TokenService,
                useClass: CustomTokenService,
            },
            {
                deps: [DOCUMENT],
                provide: IS_EPISERVER,
                useFactory: isEpiserverFactory,
            },
            {
                provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
                useValue: { duration: 3000 },
            },
            {
                provide: OverlayContainer,
                useClass: AppOverlayContainer,
            },
            provideAnimations(),
            provideNativeDateAdapter(),
            provideHttpClient(withInterceptorsFromDi()),
            provideRouter(
                routes,
                //withDebugTracing(),
                withRouterConfig({
                    paramsInheritanceStrategy: 'always',
                }),
                withInMemoryScrolling({
                    scrollPositionRestoration: 'top',
                })
            ),
        ],
    };

    if (!environment.production) {
        bootstrapConfig.providers.push(
            {
                provide: ErrorHandler,
                useValue: Sentry.createErrorHandler(),
            },
            {
                deps: [Router],
                provide: Sentry.TraceService,
            },
            {
                deps: [Sentry.TraceService],
                multi: true,
                provide: APP_INITIALIZER,
                useFactory: () => () => {},
            }
        );
    }

    return bootstrapConfig;
};
